import React from 'react';
import { 
  Box, 
  Container, 
  Typography, 
  Button, 
  Grid, 
  Card, 
  CardMedia,
  Breadcrumbs,
  Link as MuiLink,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Fade,
  Stack,
  Tooltip,
  IconButton,
  Zoom
} from '@mui/material';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { 
  ArrowBack, 
  Schedule, 
  CheckCircle, 
  Timeline,
  Stars,
  WhatsApp,
  Phone,
  Home,
  ContactPhone
} from '@mui/icons-material';
import { services } from '../data/services';

const ServiceDetail = () => {
  const { serviceId } = useParams();
  const navigate = useNavigate();
  
  const service = services.find(s => 
    s.title.toLowerCase().replace(/\s+/g, '-') === serviceId
  );

  if (!service) {
    return (
      <Container maxWidth="lg" sx={{ py: 8, textAlign: 'center' }}>
        <Typography variant="h4" gutterBottom color="error">
          Service Not Found
        </Typography>
        <Button
          variant="contained"
          startIcon={<ArrowBack />}
          onClick={() => navigate('/services')}
          sx={{ mt: 2 }}
        >
          Back to Services
        </Button>
      </Container>
    );
  }

  return (
    <Box sx={{ py: { xs: 4, md: 6 } }}>
      <Container maxWidth="lg">
        {/* Breadcrumbs */}
        <Breadcrumbs sx={{ 
          mb: 3,
          '& .MuiTypography-root': {
            fontSize: { xs: '1rem', sm: '1.1rem' }
          }
        }}>
          <MuiLink 
            component={Link} 
            to="/" 
            color="inherit"
            sx={{ 
              display: 'flex', 
              alignItems: 'center',
              '&:hover': { color: 'primary.main' }
            }}
          >
            <Home sx={{ mr: 0.5 }} fontSize="small" />
            Home
          </MuiLink>
          <MuiLink 
            component={Link} 
            to="/services" 
            color="inherit"
            sx={{ '&:hover': { color: 'primary.main' } }}
          >
            Services
          </MuiLink>
          <Typography color="text.primary">{service.title}</Typography>
        </Breadcrumbs>

        {/* Hero Section */}
        <Fade in timeout={1000}>
          <Card sx={{ 
            mb: 6, 
            borderRadius: 4, 
            overflow: 'hidden',
            boxShadow: '0 4px 24px rgba(0,0,0,0.1)'
          }}>
            <CardMedia
              component="img"
              height={{ xs: '300', sm: '400', md: '500' }}
              image={service.image}
              alt={service.title}
              sx={{ 
                objectFit: 'cover',
                transition: 'transform 0.3s ease-in-out',
                '&:hover': {
                  transform: 'scale(1.02)'
                }
              }}
            />
          </Card>
        </Fade>

        {/* Content */}
        <Grid container spacing={4}>
          <Grid item xs={12} md={8}>
            <Fade in timeout={1000}>
              <Box>
                <Typography 
                  variant="h2" 
                  gutterBottom 
                  sx={{ 
                    fontWeight: 700,
                    fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
                    background: 'linear-gradient(45deg, #009688, #4CAF50)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    mb: { xs: 2, sm: 3 }
                  }}
                >
                  {service.title}
                </Typography>
                <Typography 
                  variant="body1" 
                  paragraph 
                  sx={{ 
                    fontSize: { xs: '1.1rem', sm: '1.2rem' }, 
                    lineHeight: 1.8,
                    color: 'text.secondary',
                    mb: { xs: 3, sm: 4 }
                  }}
                >
                  {service.longDescription || service.description}
                </Typography>

                {/* Benefits Section */}
                <Paper 
                  elevation={0} 
                  sx={{ 
                    p: { xs: 3, sm: 4 }, 
                    mt: 4, 
                    borderRadius: 4,
                    border: '1px solid',
                    borderColor: 'divider',
                    background: 'linear-gradient(135deg, rgba(0,150,136,0.05) 0%, rgba(76,175,80,0.05) 100%)',
                    transition: 'transform 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-5px)',
                      boxShadow: '0 8px 24px rgba(0,150,136,0.15)'
                    }
                  }}
                >
                  <Typography 
                    variant="h4" 
                    gutterBottom 
                    sx={{ 
                      fontWeight: 600,
                      color: 'primary.main',
                      mb: 3,
                      fontSize: { xs: '1.75rem', sm: '2rem' },
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1
                    }}
                  >
                    <Stars />
                    Key Benefits
                  </Typography>
                  <List>
                    {service.benefits?.map((benefit, index) => (
                      <ListItem 
                        key={index} 
                        sx={{
                          py: { xs: 1.5, sm: 1 },
                          px: 0
                        }}
                      >
                        <ListItemIcon>
                          <CheckCircle color="primary" sx={{ fontSize: { xs: 24, sm: 20 } }} />
                        </ListItemIcon>
                        <ListItemText 
                          primary={benefit}
                          sx={{
                            '& .MuiListItemText-primary': {
                              fontSize: { xs: '1.1rem', sm: '1.1rem' }
                            }
                          }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Paper>

                {/* Features Section */}
                <Paper 
                  elevation={0} 
                  sx={{ 
                    p: { xs: 3, sm: 4 }, 
                    mt: 4, 
                    borderRadius: 4,
                    border: '1px solid',
                    borderColor: 'divider',
                    background: 'linear-gradient(135deg, rgba(76,175,80,0.05) 0%, rgba(0,150,136,0.05) 100%)',
                    transition: 'transform 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-5px)',
                      boxShadow: '0 8px 24px rgba(0,150,136,0.15)'
                    }
                  }}
                >
                  <Typography 
                    variant="h4" 
                    gutterBottom 
                    sx={{ 
                      fontWeight: 600,
                      color: 'primary.main',
                      mb: 3,
                      fontSize: { xs: '1.75rem', sm: '2rem' },
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1
                    }}
                  >
                    <Timeline />
                    Key Features
                  </Typography>
                  <List>
                    {service.features?.map((feature, index) => (
                      <ListItem 
                        key={index} 
                        sx={{
                          py: { xs: 1.5, sm: 1 },
                          px: 0
                        }}
                      >
                        <ListItemIcon>
                          <CheckCircle color="primary" sx={{ fontSize: { xs: 24, sm: 20 } }} />
                        </ListItemIcon>
                        <ListItemText 
                          primary={feature}
                          sx={{
                            '& .MuiListItemText-primary': {
                              fontSize: { xs: '1.1rem', sm: '1.1rem' }
                            }
                          }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Paper>
              </Box>
            </Fade>
          </Grid>

          {/* Sidebar */}
          <Grid item xs={12} md={4}>
            <Stack spacing={3}>
              <Fade in timeout={1000} style={{ transitionDelay: '500ms' }}>
                <Card sx={{ 
                  p: { xs: 3, sm: 4 }, 
                  borderRadius: 4,
                  background: 'linear-gradient(45deg, #009688, #4CAF50)',
                  color: 'white',
                  boxShadow: '0 4px 24px rgba(0,150,136,0.2)'
                }}>
                  <Typography 
                    variant="h5" 
                    gutterBottom
                    sx={{ 
                      fontWeight: 600,
                      mb: 2
                    }}
                  >
                    Book an Appointment
                  </Typography>
                  <Typography 
                    variant="body1" 
                    paragraph
                    sx={{ 
                      fontSize: '1.1rem',
                      opacity: 0.9,
                      mb: 3
                    }}
                  >
                    Ready to start your treatment? Book an appointment with our experienced physiotherapists.
                  </Typography>
                  <Button
                    variant="contained"
                    fullWidth
                    startIcon={<Schedule />}
                    onClick={() => navigate('/book-appointment')}
                    sx={{ 
                      bgcolor: 'white',
                      color: 'primary.main',
                      py: 1.5,
                      borderRadius: '50px',
                      fontSize: '1.1rem',
                      fontWeight: 600,
                      '&:hover': {
                        bgcolor: 'grey.100',
                        transform: 'translateY(-2px)'
                      },
                      transition: 'all 0.3s ease'
                    }}
                  >
                    Book Appointment
                  </Button>
                </Card>
              </Fade>

              <Fade in timeout={1000} style={{ transitionDelay: '700ms' }}>
                <Card sx={{ 
                  p: { xs: 3, sm: 4 }, 
                  borderRadius: 4,
                  border: '1px solid',
                  borderColor: 'divider'
                }}>
                  <Typography 
                    variant="h5" 
                    gutterBottom
                    sx={{ 
                      fontWeight: 600,
                      color: 'primary.main',
                      mb: 3
                    }}
                  >
                    Contact Us
                  </Typography>
                  <Stack spacing={2}>
                    <Button
                      fullWidth
                      variant="outlined"
                      startIcon={<Phone />}
                      href="tel:+917619678243"
                      sx={{
                        borderRadius: '50px',
                        py: 1.5,
                        borderWidth: 2,
                        '&:hover': {
                          borderWidth: 2,
                          transform: 'translateY(-2px)'
                        }
                      }}
                    >
                      Call Now
                    </Button>
                    <Button
                      fullWidth
                      variant="outlined"
                      startIcon={<WhatsApp />}
                      href={`https://wa.me/917619678243?text=Hi, I'm interested in ${service.title} service.`}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{
                        borderRadius: '50px',
                        py: 1.5,
                        borderWidth: 2,
                        color: '#25D366',
                        borderColor: '#25D366',
                        '&:hover': {
                          borderWidth: 2,
                          borderColor: '#25D366',
                          transform: 'translateY(-2px)'
                        }
                      }}
                    >
                      WhatsApp
                    </Button>
                    <Button
                      fullWidth
                      variant="outlined"
                      onClick={() => navigate('/contact-us')}
                      startIcon={<ContactPhone />}
                      sx={{
                        borderRadius: '50px',
                        py: 1.5,
                        borderWidth: 2,
                        color: '#009688',
                        borderColor: '#009688',
                        '&:hover': {
                          borderWidth: 2,
                          borderColor: '#009688',
                          backgroundColor: 'rgba(0, 150, 136, 0.04)',
                          transform: 'translateY(-2px)'
                        }
                      }}
                    >
                      Contact Us
                    </Button>
                  </Stack>
                </Card>
              </Fade>
            </Stack>
          </Grid>
        </Grid>

        {/* Bottom Navigation */}
        <Box sx={{ 
          display: 'flex', 
          flexDirection: { xs: 'column', sm: 'row' },
          gap: { xs: 2, sm: 0 },
          justifyContent: 'space-between',
          mt: { xs: 4, sm: 6 },
          pt: { xs: 3, sm: 4 },
          borderTop: 1,
          borderColor: 'divider'
        }}>
          <Button
            variant="outlined"
            startIcon={<ArrowBack />}
            onClick={() => navigate('/services')}
            sx={{
              borderRadius: '50px',
              px: { xs: 3, sm: 4 },
              py: { xs: 1.5, sm: 1.5 },
              fontSize: { xs: '1rem', sm: 'inherit' },
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'translateX(-5px)'
              }
            }}
          >
            Back to Services
          </Button>
          <Button
            variant="contained"
            endIcon={<Schedule />}
            onClick={() => navigate('/book-appointment')}
            sx={{
              borderRadius: '50px',
              px: { xs: 3, sm: 4 },
              py: { xs: 1.5, sm: 1.5 },
              fontSize: { xs: '1rem', sm: 'inherit' },
              background: 'linear-gradient(45deg, #009688, #4CAF50)',
              transition: 'all 0.3s ease',
              '&:hover': {
                background: 'linear-gradient(45deg, #00897B, #43A047)',
                transform: 'translateY(-2px)',
                boxShadow: '0 4px 20px rgba(0,150,136,0.4)'
              }
            }}
          >
            Book Appointment
          </Button>
        </Box>
      </Container>
    </Box>
  );
};

export default ServiceDetail; 